export interface CaseStudy {
  id: string;
  industry: string;
  industryIcon: string;
  client: string;
  clientType: string;
  project: string;
  tagline: string;
  duration: string;
  engineers: number;
  imageQuery: string;
  accentColor: string;
  accentBg: string;
  accentBorder: string;
  accentText: string;

  challenge: {
    headline: string;
    details: string[];
  };

  solution: {
    headline: string;
    details: string[];
  };

  outcomes: {
    headline: string;
    metrics: { label: string; before?: string; after: string; delta: string; positive: boolean }[];
    quote?: { text: string; author: string; role: string };
  };

  techStack: string[];
  teamRoles: { role: string; count: number }[];
}

export const fullCaseStudies: CaseStudy[] = [
  {
    id: 'healthcare-medsync',
    industry: 'Healthcare & Pharma',
    industryIcon: 'ri-hospital-line',
    client: 'MedSync Health',
    clientType: 'Telemedicine Provider · Series B',
    project: 'Telemedicine Platform Rebuild',
    tagline: 'Scaling from 5,000 to 50,000 concurrent video sessions while passing HIPAA audit in 6 weeks.',
    duration: '4 months',
    engineers: 8,
    imageQuery:
      'Modern telemedicine app interface mockup on a laptop screen, clean white and teal medical UI design, soft ambient lighting, professional product photography style, minimal background',
    accentColor: 'bg-emerald-500',
    accentBg: 'bg-emerald-50',
    accentBorder: 'border-emerald-200',
    accentText: 'text-emerald-700',

    challenge: {
      headline: 'A monolith that could not scale — and compliance gaps that threatened the entire business.',
      details: [
        'Legacy monolithic architecture capped concurrent video sessions at 5,000 — far below the 50,000 needed for nationwide rollout.',
        'HIPAA compliance audits were failing due to incomplete audit logging and missing encryption-at-rest on patient metadata.',
        'The engineering team of 12 was spending 60% of their time on infrastructure fires, leaving almost no capacity for product innovation.',
        'Third-party video SDK costs were scaling linearly with users, making the unit economics unsustainable.',
      ],
    },

    solution: {
      headline: 'Microservices architecture on AWS with custom video infrastructure and end-to-end compliance.',
      details: [
        'Rebuilt the platform as a microservices mesh on AWS ECS with auto-scaling groups, decoupling video, scheduling, billing, and patient records.',
        'Built a custom WebRTC video relay cluster on EC2 with dynamic scaling, cutting third-party costs by 78%.',
        'Implemented comprehensive audit logging with AWS CloudTrail + custom event pipelines, achieving 100% traceability for HIPAA auditors.',
        'Migrated patient metadata to encrypted S3 with AWS KMS and enforced field-level encryption on all PHI fields in DynamoDB.',
        'Delivered a zero-downtime migration strategy using blue-green deployments, ensuring 99.99% uptime throughout the transition.',
      ],
    },

    outcomes: {
      headline: 'Nationwide rollout enabled, audit passed, and infrastructure costs cut by nearly 80%.',
      metrics: [
        { label: 'Concurrent Sessions', before: '5,000', after: '50,000+', delta: '+900%', positive: true },
        { label: 'Uptime', before: '97.2%', after: '99.99%', delta: '+2.79pp', positive: true },
        { label: 'Video Infrastructure Cost', before: '$48K/mo', after: '$10.5K/mo', delta: '-78%', positive: true },
        { label: 'HIPAA Audit', before: 'Failing', after: 'Passed', delta: '6 weeks', positive: true },
      ],
      quote: {
        text: 'The team Thoughtic placed understood HIPAA compliance at a code level. They did not just write features — they wrote audit-safe features.',
        author: 'Dr. Sarah Lin',
        role: 'CTO, MedSync Health',
      },
    },

    techStack: ['AWS ECS', 'WebRTC', 'Node.js', 'DynamoDB', 'React Native', 'AWS KMS'],
    teamRoles: [
      { role: 'Senior Backend', count: 3 },
      { role: 'DevOps / SRE', count: 2 },
      { role: 'Mobile Engineer', count: 2 },
      { role: 'Security Engineer', count: 1 },
    ],
  },

  {
    id: 'banking-vertex',
    industry: 'Banking & Financial Services',
    industryIcon: 'ri-bank-line',
    client: 'Vertex Capital',
    clientType: 'Investment Bank · Public',
    project: 'Real-Time Payment Gateway',
    tagline: 'Replacing 24-hour batch settlement with event-driven T+0 processing at 12,000 transactions per second.',
    duration: '6 months',
    engineers: 12,
    imageQuery:
      'Futuristic fintech dashboard with charts and payment flows, dark navy UI with amber accent charts, glowing data streams, professional digital product render',
    accentColor: 'bg-amber-500',
    accentBg: 'bg-amber-50',
    accentBorder: 'border-amber-200',
    accentText: 'text-amber-700',

    challenge: {
      headline: 'A batch-based settlement system that could not keep up with real-time customer expectations.',
      details: [
        'All payment settlements ran in overnight batches, creating a 24-hour delay that customers increasingly refused to accept.',
        'Fraud detection was reactive — rules-based and run after the fact, missing 30% of fraudulent transactions.',
        'The core banking system had a single point of failure: one Oracle database handling all transaction logs.',
        'Peak load during quarterly dividend payouts caused 4-hour system outages, damaging institutional client trust.',
      ],
    },

    solution: {
      headline: 'Event-driven architecture with streaming fraud detection and distributed transaction ledgers.',
      details: [
        'Designed an event-driven payment platform using Apache Kafka as the backbone, enabling real-time settlement and instant reconciliation.',
        'Built a streaming ML fraud-detection pipeline on Apache Flink with sub-200ms latency, catching 94% of fraudulent patterns in real time.',
        'Migrated transaction ledger to a distributed PostgreSQL cluster with read replicas and automated failover, eliminating the single point of failure.',
        'Implemented idempotent payment APIs with exactly-once processing guarantees, preventing double-charge scenarios during network partitions.',
        'Created a real-time settlement dashboard for operations teams with live transaction flow visualization and anomaly alerting.',
      ],
    },

    outcomes: {
      headline: 'Settlement is now real-time. Fraud detection is proactive. System outages are history.',
      metrics: [
        { label: 'Settlement Time', before: 'T+24 hours', after: 'T+0 (real-time)', delta: '-24h', positive: true },
        { label: 'Fraud Detection Latency', before: '15 minutes', after: '200ms', delta: '-99.8%', positive: true },
        { label: 'Peak Transaction Rate', before: '800 TPS', after: '12,000 TPS', delta: '+1,400%', positive: true },
        { label: 'System Uptime (Dividend Days)', before: '72%', after: '99.97%', delta: '+27.97pp', positive: true },
      ],
      quote: {
        text: 'These engineers came in understanding capital markets, not just code. That domain fluency saved us months of ramp-up time.',
        author: 'James Thornton',
        role: 'Head of Engineering, Vertex Capital',
      },
    },

    techStack: ['Java / Spring Boot', 'Apache Kafka', 'Apache Flink', 'PostgreSQL', 'Redis', 'Kubernetes'],
    teamRoles: [
      { role: 'Senior Java Engineer', count: 4 },
      { role: 'Data Engineer', count: 3 },
      { role: 'Platform Engineer', count: 3 },
      { role: 'QA / Test Lead', count: 2 },
    ],
  },

  {
    id: 'retail-luxe',
    industry: 'Retail & E-commerce',
    industryIcon: 'ri-shopping-cart-line',
    client: 'LuxeMarket',
    clientType: 'Luxury E-commerce · Series C',
    project: 'Omnichannel Commerce Platform',
    tagline: 'Rebuilding checkout from 1.2% to 4.8% mobile conversion while handling 50x flash-sale traffic.',
    duration: '5 months',
    engineers: 10,
    imageQuery:
      'Sleek e-commerce website displayed on a large monitor, luxury product grid layout, warm neutral tones with gold accents, professional studio photography style, soft shadows',
    accentColor: 'bg-rose-500',
    accentBg: 'bg-rose-50',
    accentBorder: 'border-rose-200',
    accentText: 'text-rose-700',

    challenge: {
      headline: 'A luxury shopping experience that was bleeding customers and breaking during its biggest moments.',
      details: [
        'Mobile conversion rate was stuck at 1.2% — 3x below industry average — due to a clunky 7-step checkout flow.',
        'Inventory oversold across 3 warehouses daily because sync ran on a 15-minute cron job, creating fulfillment chaos.',
        'The site crashed during flash sales — the exact moments when brand equity and revenue were on the line.',
        'The monolithic Magento backend made it impossible to A/B test checkout changes without full deploy cycles.',
      ],
    },

    solution: {
      headline: 'Headless commerce with real-time inventory, one-click checkout, and flash-sale-grade elasticity.',
      details: [
        'Rebuilt the storefront as a Next.js headless frontend decoupled from a custom Node.js commerce API, enabling rapid A/B testing and iterative checkout optimization.',
        'Designed a real-time inventory sync using Redis Streams + WebSocket push, eliminating overselling with sub-second consistency across warehouses.',
        'Implemented a one-click checkout with Apple Pay / Google Pay integration, reducing the flow from 7 steps to 2 taps.',
        'Built an auto-scaling infrastructure on AWS with pre-warming for flash-sale events, handling 50x baseline traffic without degradation.',
        'Added a personalized recommendation engine using Elasticsearch + customer behavior signals, driving 18% higher average order value.',
      ],
    },

    outcomes: {
      headline: 'Conversion tripled. Flash sales became revenue events instead of outages.',
      metrics: [
        { label: 'Mobile Conversion', before: '1.2%', after: '4.8%', delta: '+300%', positive: true },
        { label: 'Inventory Oversell Rate', before: '8.4%', after: '0.02%', delta: '-99.8%', positive: true },
        { label: 'Flash-Sale Uptime', before: 'Crashed', after: '100%', delta: '50x load handled', positive: true },
        { label: 'Checkout Steps', before: '7', after: '2', delta: '-71%', positive: true },
      ],
      quote: {
        text: 'The checkout rebuild alone paid for the entire engagement in the first quarter. And the team was shipping daily from week two.',
        author: 'Elena Voss',
        role: 'VP Product, LuxeMarket',
      },
    },

    techStack: ['Next.js', 'Node.js', 'Redis', 'Elasticsearch', 'AWS Lambda', 'Stripe'],
    teamRoles: [
      { role: 'Frontend Engineer', count: 4 },
      { role: 'Backend Engineer', count: 3 },
      { role: 'Mobile Engineer', count: 2 },
      { role: 'Cloud Architect', count: 1 },
    ],
  },

  {
    id: 'saas-flowstack',
    industry: 'SaaS & Product Companies',
    industryIcon: 'ri-apps-line',
    client: 'FlowStack SaaS',
    clientType: 'B2B Workflow Platform · Series A',
    project: 'Multi-Tenant Platform Rewrite',
    tagline: 'Cutting customer onboarding from 3 weeks to 15 minutes and reducing infrastructure cost per tenant by 68%.',
    duration: '5 months',
    engineers: 11,
    imageQuery:
      'SaaS product dashboard with user analytics charts, clean white UI with teal accent buttons, modern B2B software interface, professional product photography on minimal background',
    accentColor: 'bg-primary-500',
    accentBg: 'bg-primary-50',
    accentBorder: 'border-primary-200',
    accentText: 'text-primary-700',

    challenge: {
      headline: 'Single-tenant architecture that was choking growth and inflating infrastructure bills.',
      details: [
        'Every new customer required a manual 3-week provisioning process: new EC2 instance, new database, new deployment pipeline.',
        'Infrastructure costs scaled linearly with customers, meaning gross margins shrank as the company grew — the opposite of SaaS economics.',
        'Feature releases required coordination across 200+ isolated deployments, making weekly releases practically impossible.',
        'Net Revenue Retention was stuck at 102% because customers could not easily add seats or upgrade without re-provisioning.',
      ],
    },

    solution: {
      headline: 'True multi-tenancy with tenant-isolated data, shared compute, and instant provisioning.',
      details: [
        'Redesigned the platform as a true multi-tenant architecture using row-level security in PostgreSQL and tenant-aware middleware, keeping data isolated while sharing compute.',
        'Built an automated tenant provisioning API that spins up a new customer environment in under 15 minutes via Terraform + AWS APIs.',
        'Implemented feature flags with LaunchDarkly, enabling per-tenant feature rollout without code deploys.',
        'Redesigned the billing engine to support usage-based metering, seat-based tiers, and hybrid pricing models — all configurable per tenant.',
        'Created a tenant analytics dashboard showing per-customer compute costs, enabling the sales team to price deals with full cost visibility.',
      ],
    },

    outcomes: {
      headline: 'Onboarding became instant. Margins expanded. NRR jumped 22 points.',
      metrics: [
        { label: 'Onboarding Time', before: '3 weeks', after: '15 minutes', delta: '-99.5%', positive: true },
        { label: 'Infra Cost per Customer', before: '$340/mo', after: '$109/mo', delta: '-68%', positive: true },
        { label: 'Net Revenue Retention', before: '102%', after: '124%', delta: '+22pp', positive: true },
        { label: 'Release Cycle', before: 'Monthly', after: 'Weekly', delta: '4x faster', positive: true },
      ],
      quote: {
        text: 'Multi-tenancy is not just architecture — it is a business model unlock. These engineers understood that from day one.',
        author: 'Marcus Reid',
        role: 'Founder & CEO, FlowStack',
      },
    },

    techStack: ['React', 'Node.js', 'PostgreSQL', 'Terraform', 'AWS ECS', 'LaunchDarkly'],
    teamRoles: [
      { role: 'Full-Stack Engineer', count: 5 },
      { role: 'Frontend Engineer', count: 3 },
      { role: 'Platform Engineer', count: 2 },
      { role: 'DevOps', count: 1 },
    ],
  },

  {
    id: 'logistics-swift',
    industry: 'Logistics & Supply Chain',
    industryIcon: 'ri-truck-line',
    client: 'SwiftFreight',
    clientType: 'Fleet Management · Private',
    project: 'AI Route Optimization Engine',
    tagline: 'Boosting fleet utilization from 62% to 91% and cutting driver idle time by 78% with ML-powered routing.',
    duration: '3 months',
    engineers: 6,
    imageQuery:
      'Logistics control center with map screens showing truck routes, modern dark dashboard UI with green route lines, industrial warehouse atmosphere, professional tech photography',
    accentColor: 'bg-yellow-500',
    accentBg: 'bg-yellow-50',
    accentBorder: 'border-yellow-200',
    accentText: 'text-yellow-700',

    challenge: {
      headline: 'A fleet of 800 trucks operating with the efficiency of a much smaller operation.',
      details: [
        'Fleet capacity utilization was only 62% — 800 trucks were effectively doing the work of 500.',
        'Driver idle time averaged 3.2 hours per day due to poor dispatch coordination and lack of real-time route adjustments.',
        'Route planning was done manually by a team of 14 dispatchers using spreadsheets and phone calls — a process that took 4 hours every morning.',
        'Fuel costs were 18% above industry benchmark because routes were not optimized for traffic, weather, or delivery-window constraints.',
      ],
    },

    solution: {
      headline: 'End-to-end ML routing pipeline that optimizes in real time based on live traffic, weather, and delivery constraints.',
      details: [
        'Built a route-optimization engine using Google OR-Tools + custom heuristics, solving the Vehicle Routing Problem with Time Windows for 800+ trucks in under 3 minutes.',
        'Integrated live traffic (Google Maps API), weather data (OpenWeatherMap), and delivery-window constraints into the optimization model.',
        'Created a driver mobile app with turn-by-turn navigation, real-time rerouting, and delivery confirmation with photo proof.',
        'Built a dispatcher dashboard showing fleet status, predicted arrival times, and automatic alerts for delays or deviations.',
        'Implemented predictive maintenance alerts by correlating route strain data with vehicle telemetry, preventing 12 breakdowns in the first month.',
      ],
    },

    outcomes: {
      headline: 'The fleet now operates like a machine — literally.',
      metrics: [
        { label: 'Fleet Utilization', before: '62%', after: '91%', delta: '+46.8%', positive: true },
        { label: 'Driver Idle Time', before: '3.2 hrs/day', after: '0.7 hrs/day', delta: '-78%', positive: true },
        { label: 'Fuel Cost Reduction', before: 'Baseline', after: '-18%', delta: 'Q1 result', positive: true },
        { label: 'Route Planning Time', before: '4 hours', after: '3 minutes', delta: '-98.8%', positive: true },
      ],
      quote: {
        text: 'We did not just get engineers — we got logistics engineers. They understood dispatch, driver incentives, and fuel economics.',
        author: 'Derek Ngo',
        role: 'COO, SwiftFreight',
      },
    },

    techStack: ['Python', 'TensorFlow', 'Google OR-Tools', 'React Native', 'PostgreSQL', 'Google Maps API'],
    teamRoles: [
      { role: 'ML Engineer', count: 2 },
      { role: 'Backend Engineer', count: 2 },
      { role: 'Mobile Engineer', count: 1 },
      { role: 'Data Analyst', count: 1 },
    ],
  },

  {
    id: 'manufacturing-aero',
    industry: 'Manufacturing',
    industryIcon: 'ri-tools-line',
    client: 'AeroForm Industries',
    clientType: 'Aerospace Components · Fortune 500',
    project: 'Predictive Maintenance Platform',
    tagline: 'Unifying 2,400 sensors into a single predictive model that cut unplanned downtime by 74%.',
    duration: '5 months',
    engineers: 7,
    imageQuery:
      'Industrial IoT dashboard showing factory machine telemetry, dark UI with orange warning indicators, manufacturing floor background blurred, professional product photography',
    accentColor: 'bg-orange-500',
    accentBg: 'bg-orange-50',
    accentBorder: 'border-orange-200',
    accentText: 'text-orange-700',

    challenge: {
      headline: '$2.1M in annual losses from unplanned downtime — and no unified view of the factory floor.',
      details: [
        'Unplanned downtime on critical CNC and stamping machines cost the company $2.1M annually in lost production and expedited replacement parts.',
        'Maintenance was purely calendar-based: machines were serviced every 90 days regardless of actual condition, leading to both unnecessary work and missed failures.',
        '2,400 IoT sensors across the factory floor fed into 6 disconnected data silos, making holistic analysis impossible.',
        'When a machine did fail, root-cause analysis took an average of 8 hours because sensor history was scattered across systems.',
      ],
    },

    solution: {
      headline: 'Unified IoT data lake with ML-powered anomaly detection and a factory-floor dashboard for maintenance teams.',
      details: [
        'Built a centralized data lake on Azure IoT Hub that ingested real-time telemetry from all 2,400 sensors, normalizing 14 different data formats into a unified schema.',
        'Trained an LSTM-based anomaly-detection model on 3 years of historical sensor data, achieving 87% accuracy in predicting failures 24–72 hours in advance.',
        'Created a factory-floor dashboard in Grafana showing real-time machine health, predicted failure probability, and recommended maintenance actions per machine.',
        'Implemented an automated work-order system that generated maintenance tickets directly in the existing SAP CMMS when the model predicted a failure.',
        'Built a digital twin simulation layer that let engineers test maintenance scenarios virtually before scheduling actual downtime.',
      ],
    },

    outcomes: {
      headline: 'Downtime became predictable — and preventable.',
      metrics: [
        { label: 'Unplanned Downtime', before: '$2.1M/yr', after: '$546K/yr', delta: '-74%', positive: true },
        { label: 'Failure Prediction Accuracy', before: 'N/A', after: '87%', delta: '24-72h advance', positive: true },
        { label: 'Root-Cause Analysis', before: '8 hours', after: '45 minutes', delta: '-90.6%', positive: true },
        { label: 'Maintenance Cost', before: 'Baseline', after: '-$840K', delta: 'Year 1 savings', positive: true },
      ],
      quote: {
        text: 'They did not just build dashboards — they built a maintenance culture. Our technicians now trust the data more than the calendar.',
        author: 'Helmut Bauer',
        role: 'Director of Operations, AeroForm',
      },
    },

    techStack: ['C++', 'Python', 'Azure IoT Hub', 'Grafana', 'PostgreSQL', 'SAP CMMS'],
    teamRoles: [
      { role: 'Embedded Engineer', count: 2 },
      { role: 'Data Engineer', count: 2 },
      { role: 'Backend Engineer', count: 2 },
      { role: 'DevOps', count: 1 },
    ],
  },

  {
    id: 'edtech-learnsphere',
    industry: 'EdTech',
    industryIcon: 'ri-graduation-cap-line',
    client: 'LearnSphere',
    clientType: 'Online Learning Platform · Series B',
    project: 'Adaptive Learning Platform',
    tagline: 'Personalizing learning paths to triple course completion rates and cut instructor intervention time by 40%.',
    duration: '4 months',
    engineers: 9,
    imageQuery:
      'Modern e-learning platform interface on a tablet, colorful student progress cards, friendly educational UI with warm yellow accents, clean white background, product photography',
    accentColor: 'bg-pink-500',
    accentBg: 'bg-pink-50',
    accentBorder: 'border-pink-200',
    accentText: 'text-pink-700',

    challenge: {
      headline: 'A one-size-fits-all platform where most students dropped out before finishing.',
      details: [
        'Course completion rate was 23% — meaning 3 out of 4 students abandoned their learning path before finishing.',
        'Content was static and linear: every student saw the same sequence of videos and quizzes regardless of their prior knowledge or learning pace.',
        'Instructors had no real-time visibility into student progress, meaning struggling students were only identified after they had already dropped out.',
        'The existing recommendation engine was a simple tag-based filter that suggested courses, not personalized learning paths within a course.',
      ],
    },

    solution: {
      headline: 'Adaptive algorithm that personalizes every lesson, quiz, and intervention in real time.',
      details: [
        'Built an adaptive learning algorithm using reinforcement learning that adjusted content difficulty, pacing, and format (video vs. text vs. interactive) based on real-time student performance signals.',
        'Implemented a knowledge-graph model that mapped 12,000+ learning concepts and their prerequisite relationships, enabling the system to identify and remediate knowledge gaps dynamically.',
        'Created an instructor dashboard showing real-time student engagement heatmaps, at-risk student alerts, and automated intervention suggestions.',
        'Built a gamification layer with adaptive milestones, peer comparison, and streak rewards that increased daily active study time by 34%.',
        'Added WebRTC-powered live study rooms where students could collaborate on problems, matched by skill level and learning goals.',
      ],
    },

    outcomes: {
      headline: 'Learning became personal — and students actually finished.',
      metrics: [
        { label: 'Course Completion', before: '23%', after: '67%', delta: '+191%', positive: true },
        { label: 'Instructor Intervention Time', before: '2.4 hrs/week', after: '1.4 hrs/week', delta: '-40%', positive: true },
        { label: 'Daily Study Time', before: '18 min', after: '24 min', delta: '+34%', positive: true },
        { label: 'Student NPS', before: '42', after: '68', delta: '+62%', positive: true },
      ],
      quote: {
        text: 'The adaptive engine does what our best instructors always wished they had time to do: give every student a personal tutor.',
        author: 'Priya Sharma',
        role: 'Head of Pedagogy, LearnSphere',
      },
    },

    techStack: ['React', 'Node.js', 'MongoDB', 'WebRTC', 'Python', 'TensorFlow'],
    teamRoles: [
      { role: 'Full-Stack Engineer', count: 4 },
      { role: 'Frontend Engineer', count: 2 },
      { role: 'ML Engineer', count: 2 },
      { role: 'UX Engineer', count: 1 },
    ],
  },

  {
    id: 'agritech-greenyield',
    industry: 'AgriTech',
    industryIcon: 'ri-plant-line',
    client: 'GreenYield Farms',
    clientType: 'Precision Agriculture Co-op · Private',
    project: 'Precision Irrigation System',
    tagline: 'Cutting water usage by 32% and improving yield prediction accuracy from 62% to 91% with IoT + satellite data.',
    duration: '3 months',
    engineers: 5,
    imageQuery:
      'Agriculture technology dashboard showing crop health maps, green and earth-tone UI with satellite imagery widgets, clean modern design, professional tech photography on light background',
    accentColor: 'bg-lime-500',
    accentBg: 'bg-lime-50',
    accentBorder: 'border-lime-200',
    accentText: 'text-lime-700',

    challenge: {
      headline: 'Wasting water and guessing yields in an industry where both cost money.',
      details: [
        'Water usage was 40% above optimal because irrigation schedules were based on fixed timers, not actual soil moisture or weather forecasts.',
        'Yield predictions were manual and inaccurate — farmers used last year\'s numbers plus intuition, achieving only 62% accuracy against actual harvest.',
        'No real-time visibility into soil-moisture variance across the 4,200-acre farm, leading to both overwatering and underwatering in different zones.',
        'Satellite imagery from existing providers was too low-resolution and too infrequent to guide day-to-day irrigation decisions.',
      ],
    },

    solution: {
      headline: 'IoT soil sensors + high-resolution satellite imagery + ML yield forecasting in a single farm dashboard.',
      details: [
        'Deployed 180 IoT soil-moisture sensors across the farm, transmitting real-time data via LoRaWAN to a central AWS IoT Core pipeline.',
        'Integrated Sentinel-2 satellite imagery at 10m resolution with weekly refresh, processed through an AWS Lambda pipeline for NDVI and moisture-index calculations.',
        'Built an ML yield-prediction model combining sensor data, satellite indices, weather forecasts, and historical harvest records — achieving 91% accuracy 6 weeks before harvest.',
        'Created a farm-management dashboard showing zone-by-zone moisture levels, recommended irrigation schedules, and predicted yield maps.',
        'Implemented automated valve-control integration with the existing drip-irrigation system, enabling scheduled and sensor-triggered watering.',
      ],
    },

    outcomes: {
      headline: 'Every drop of water now has a data-driven purpose.',
      metrics: [
        { label: 'Water Usage', before: '140% of optimal', after: '108% of optimal', delta: '-32%', positive: true },
        { label: 'Yield Prediction Accuracy', before: '62%', after: '91%', delta: '+46.8%', positive: true },
        { label: 'Irrigation Labor Time', before: '28 hrs/week', after: '4 hrs/week', delta: '-85.7%', positive: true },
        { label: 'Crop Loss from Drought', before: '12%', after: '3%', delta: '-75%', positive: true },
      ],
      quote: {
        text: 'For the first time, we are making irrigation decisions based on what is actually happening in the soil, not what happened last year.',
        author: 'Tom Whitfield',
        role: 'Farm Operations Director, GreenYield',
      },
    },

    techStack: ['Python', 'React', 'AWS IoT Core', 'LoRaWAN', 'TensorFlow', 'QGIS'],
    teamRoles: [
      { role: 'IoT Engineer', count: 2 },
      { role: 'Data / ML Engineer', count: 2 },
      { role: 'Frontend Engineer', count: 1 },
    ],
  },

  {
    id: 'telecom-nexa',
    industry: 'Telecom',
    industryIcon: 'ri-smartphone-line',
    client: 'NexaCom Networks',
    clientType: '5G Network Provider · Public',
    project: '5G Edge Service Orchestrator',
    tagline: 'Enabling real-time 5G network slicing and cutting service provisioning from 48 hours to 90 seconds.',
    duration: '6 months',
    engineers: 9,
    imageQuery:
      '5G network visualization with glowing node connections, dark futuristic UI with cyan and white light trails, professional tech concept art style, high detail render',
    accentColor: 'bg-red-500',
    accentBg: 'bg-red-50',
    accentBorder: 'border-red-200',
    accentText: 'text-red-700',

    challenge: {
      headline: 'A legacy BSS that could not support the speed and flexibility 5G customers demanded.',
      details: [
        'Legacy Business Support System (BSS) was built for static 4G plans and could not support dynamic 5G network slicing — a core 5G selling point.',
        'Service provisioning for enterprise customers took 48 hours because it required manual configuration across 7 different systems.',
        'Customer churn was 14% annually, driven primarily by enterprise customers moving to competitors with faster provisioning and more flexible plans.',
        'The monolithic BSS architecture made it impossible to roll out new service bundles without 3-month release cycles.',
      ],
    },

    solution: {
      headline: 'Cloud-native BSS with real-time network slicing, instant provisioning, and automated service orchestration.',
      details: [
        'Redesigned the BSS as a cloud-native microservices architecture on Kubernetes, with independent services for catalog, provisioning, billing, and network orchestration.',
        'Built a real-time network-slicing API using gRPC that allows enterprise customers to create, resize, and tear down network slices via self-service portal in under 90 seconds.',
        'Implemented an event-driven service orchestration layer using Apache Kafka that coordinates across network functions, billing, and CRM automatically.',
        'Created a self-service enterprise portal where customers can configure QoS parameters, monitor slice performance, and receive real-time usage alerts.',
        'Migrated all customer data to a distributed PostgreSQL cluster with automated failover, eliminating the 4-hour maintenance windows that previously disrupted service.',
      ],
    },

    outcomes: {
      headline: '5G became a self-service product — not a 48-hour ticket.',
      metrics: [
        { label: 'Service Provisioning', before: '48 hours', after: '90 seconds', delta: '-99.9%', positive: true },
        { label: 'Customer Churn', before: '14%', after: '6%', delta: '-57%', positive: true },
        { label: 'New Bundle Release Cycle', before: '3 months', after: '2 weeks', delta: '-83%', positive: true },
        { label: 'Enterprise NPS', before: '31', after: '58', delta: '+87%', positive: true },
      ],
      quote: {
        text: 'We went from being the slowest provider to the fastest in our market. The self-service portal changed how our sales team sells.',
        author: 'Raj Patel',
        role: 'Head of Product, NexaCom Networks',
      },
    },

    techStack: ['Java / Spring Boot', 'Kubernetes', 'gRPC', 'Apache Kafka', 'PostgreSQL', 'React'],
    teamRoles: [
      { role: 'Java Engineer', count: 3 },
      { role: 'Cloud / K8s Engineer', count: 2 },
      { role: 'Backend Engineer', count: 2 },
      { role: 'Network Engineer', count: 2 },
    ],
  },

  {
    id: 'insurance-shieldsure',
    industry: 'Insurance',
    industryIcon: 'ri-shield-check-line',
    client: 'ShieldSure Insurance',
    clientType: 'P&C Insurer · Fortune 1000',
    project: 'Claims Automation Platform',
    tagline: 'Cutting claims processing from 14 days to 18 hours with ML auto-adjudication and 94% fraud accuracy.',
    duration: '4 months',
    engineers: 7,
    imageQuery:
      'Insurance claims dashboard with document scanning AI visualization, clean white and navy UI with green status indicators, professional fintech product photography, soft lighting',
    accentColor: 'bg-teal-500',
    accentBg: 'bg-teal-50',
    accentBorder: 'border-teal-200',
    accentText: 'text-teal-700',

    challenge: {
      headline: 'A claims process that frustrated customers, burned adjusters, and bled money to fraud.',
      details: [
        'Claims processing averaged 14 days from submission to payout — 3x the industry benchmark — because every claim required manual adjuster review.',
        'Fraud detection was a manual checklist process that missed 30% of fraudulent claims, costing the company $18M annually in undetected fraud.',
        'Customer satisfaction (NPS) was 62, driven primarily by lack of transparency — customers had no visibility into where their claim was in the process.',
        'The claims intake system was a collection of PDF forms and phone calls, creating data-entry bottlenecks and transcription errors.',
      ],
    },

    solution: {
      headline: 'End-to-end claims automation: intake, adjudication, fraud detection, and payout — all in one platform.',
      details: [
        'Built an AI-powered document-intake system using OCR + NLP that extracts claim details from photos, PDFs, and emails with 96% field accuracy.',
        'Implemented an ML auto-adjudication model that approves straightforward claims (fender-benders, minor property damage) in under 18 hours without human touch.',
        'Created a fraud-detection ensemble using gradient-boosted trees + anomaly detection on claim patterns, catching 94% of fraudulent submissions at intake.',
        'Built a customer-facing claims tracker with real-time status updates, document upload, and direct adjuster messaging — all in a mobile-first app.',
        'Integrated with the existing core policy system via an event-driven API gateway, ensuring data consistency without disrupting legacy workflows.',
      ],
    },

    outcomes: {
      headline: 'Claims became fast, fair, and fraud-resistant.',
      metrics: [
        { label: 'Claims Processing Time', before: '14 days', after: '18 hours', delta: '-94.6%', positive: true },
        { label: 'Fraud Detection Accuracy', before: '70%', after: '94%', delta: '+34.3%', positive: true },
        { label: 'Customer NPS', before: '62', after: '78', delta: '+25.8%', positive: true },
        { label: 'Adjuster Hours per Claim', before: '4.2 hrs', after: '1.1 hrs', delta: '-73.8%', positive: true },
      ],
      quote: {
        text: 'Our customers used to call us asking where their claim was. Now they know before they think to ask. That is the difference engineering makes.',
        author: 'Catherine Moore',
        role: 'VP Claims Operations, ShieldSure',
      },
    },

    techStack: ['Python', 'Django', 'React', 'PostgreSQL', 'Snowflake', 'AWS Textract'],
    teamRoles: [
      { role: 'Python / ML Engineer', count: 3 },
      { role: 'Full-Stack Developer', count: 2 },
      { role: 'Data Engineer', count: 1 },
      { role: 'QA Engineer', count: 1 },
    ],
  },
];

export const caseStudyStats = [
  { label: 'Projects Delivered', value: '10', suffix: 'featured case studies', icon: 'ri-folder-chart-line' },
  { label: 'Total Engineers Deployed', value: '85', suffix: 'across all engagements', icon: 'ri-group-line' },
  { label: 'Avg Engagement Duration', value: '4.3', suffix: 'months', icon: 'ri-time-line' },
  { label: 'Client Satisfaction', value: '4.9', suffix: '/ 5.0 rating', icon: 'ri-star-fill' },
];